import compareAsc from 'date-fns/compareAsc';
import {getItemAltText} from '../../accessibility/getItemAltText';
import {getEnv} from '../../common/env/env';
import {getCommonBadge} from '../../common/getCommonBadge';
import {getSubtitleList} from '../../common/getSubtitleList';
import {t} from '../../common/getTranslations';
import {ItemSeriesDto} from '../../common/shared/api/gateway/ItemSeriesDto.interface';
import {ItemSetDto} from '../../common/shared/api/gateway/ItemSetDto.interface';
import {ItemSingleDto} from '../../common/shared/api/gateway/ItemSingleDto.interface';
import {SetProductDto} from '../../common/shared/api/gateway/SetProductDto.interface';
import {PurchaseTypeVo} from '../../common/shared/api/nicolas/vo/enums/PurchaseTypeVo.enum';
import {SlideTypeVo} from '../../common/shared/api/nicolas/vo/enums/SlideTypeVo.enum';
import {getDiscountRateText} from '../../common/shared/utils/getDiscountRateText';
import {getFormattedNumberWithSuffix} from '../../common/shared/utils/getFormattedNumberWithSuffix';
import {getKageUrl} from '../../common/shared/utils/getKageUrl';
import {parseDate} from '../../common/shared/utils/parseDate';
import {
  AgeGrade,
  EventLog,
  ItemType,
  Maybe,
  MetaInfoType,
  PosterViewItem,
  SectionType,
  User,
} from '../generated/types';
import {createAgeGrade, getAgeGradeVo} from './AgeGrade';

export function createPosterViewItem(
  userInfo: Maybe<User>,
  param:
    | {
        rankingItemSeriesDto: ItemSeriesDto;
        sectionType?: SectionType;
      }
    | {
        itemSeriesDto: ItemSeriesDto;
        metaInfoType?: Maybe<MetaInfoType>;
        sectionType?: SectionType;
      }
    | {itemSingleDto: ItemSingleDto}
    | {setProductDto: SetProductDto}
    | {itemSetDto: ItemSetDto}
): PosterViewItem {
  if ('itemSeriesDto' in param) {
    return withItemSeriesDto({
      userInfo,
      ...param,
    });
  } else if ('rankingItemSeriesDto' in param) {
    return withRankingItemSeriesDto(userInfo, param.rankingItemSeriesDto);
  } else if ('setProductDto' in param) {
    return withSetProductDto(userInfo, param.setProductDto);
  } else if ('itemSetDto' in param) {
    return withItemSetDto({
      userInfo,
      ...param,
    });
  } else {
    return withItemSingleDto(userInfo, param.itemSingleDto);
  }
}

export function withItemSeriesDto({
  userInfo,
  itemSeriesDto: {
    authors,
    age_grade,
    badge,
    business_model,
    is_waitfree,
    waitfree_period_by_minute,
    series_id,
    thumbnail,
    title,
    on_issue,
    operator_property,
    service_property,
    sub_category,
    category,
  },
  metaInfoType,
  sectionType,
}: {
  userInfo: Maybe<User>;
  itemSeriesDto: ItemSeriesDto;
  metaInfoType?: Maybe<MetaInfoType>;
  sectionType?: SectionType;
}): PosterViewItem {
  const {badgeList, ageGradeBadge, statusBadge} = getCommonBadge({
    itemType: ItemType.PosterView,
    badge,
    age_grade,
    is_waitfree,
    waitfree_period_by_minute: waitfree_period_by_minute || null,
    business_model,
    self_censorship: operator_property?.self_censorship,
  });

  const helixImpId = operator_property?.helix_imp_id;
  const impId = helixImpId
    ? sectionType === SectionType.HelixTheme
      ? 'shelix'
      : helixImpId
    : undefined;

  const operatorTitle = operator_property?.title;
  const eventLog: EventLog = {
    eventMeta: {
      id: String(series_id),
      type: 'seriesId',
      provider: helixImpId ? 'helix' : '서비스',
      name: title,
      series: title,
      series_id: String(series_id),
      category,
      subcategory: sub_category,
    },
    customProps: {
      landing_path: 'contents', // 작품홈 랜딩
      view_type: 'series_poster_view',
      ...(helixImpId
        ? {
            helix_id: helixImpId,
            helix_yn: 'y',
          }
        : {}),
    },
    click: {
      imp_id: impId,
      imp_provider: helixImpId ? 'helix' : undefined,
    },
    viewimp_contents: helixImpId
      ? {
          imp_id: impId,
          imp_provider: 'helix',
          imp_type: 'vimp',
          id: String(series_id),
          type: '작품홈',
          name: title,
        }
      : undefined,
  };

  const altText = getItemAltText({
    title,
    badgeList,
    statusBadge,
    ageGradeBadge,
    subtitleList: getSubtitleList({
      itemType: ItemType.PosterView,
      metaInfoType,
      servicePropertyDto: service_property,
      onIssueVo: on_issue,
      authors,
      subCategoryName: sub_category,
      categoryName: category,
      isAltText: true,
    }),
  });

  return {
    id: `PosterView-itemSeries-${series_id}-${helixImpId}-${metaInfoType}`,
    type: ItemType.PosterView,
    ageGrade: createAgeGrade(age_grade),
    selfCensorship: operator_property?.self_censorship,
    title: operatorTitle || title, // 동일작의 경우 운영툴에서 설정한 타이틀을 우선으로 보여줍니다,
    altText,
    badgeList,
    ageGradeBadge,
    statusBadge,
    showPlayerIcon: false,
    thumbnail: thumbnail.startsWith('http')
      ? thumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: thumbnail,
          resolution: 'th3',
        }),
    subtitleList: getSubtitleList({
      itemType: ItemType.PosterView,
      metaInfoType,
      servicePropertyDto: service_property,
      onIssueVo: on_issue,
      authors,
      subCategoryName: sub_category,
      categoryName: category,
    }),
    scheme: `kakaopage://open/content?series_id=${series_id}`,
    rank: service_property?.rank ?? undefined,
    seriesId: series_id,
    eventLog,
    discountRate: operator_property?.cash_info?.discount_rate,
    discountRateText: getDiscountRateText({
      discountRate: operator_property?.cash_info?.discount_rate,
      setDiscountRate: operator_property?.cash_info?.set_discount_rate,
    }),
  };
}

export function withRankingItemSeriesDto(
  userInfo: Maybe<User>,
  {
    authors,
    age_grade,
    badge,
    business_model,
    is_waitfree,
    waitfree_period_by_minute,
    series_id,
    thumbnail,
    title,
    on_issue,
    operator_property,
    service_property,
    sub_category,
    category,
  }: ItemSeriesDto,
  metaInfoType?: Maybe<MetaInfoType>
): PosterViewItem {
  const {badgeList, ageGradeBadge, statusBadge} = getCommonBadge({
    itemType: ItemType.RankingPosterView,
    badge,
    age_grade,
    is_waitfree,
    waitfree_period_by_minute: waitfree_period_by_minute || null,
    business_model,
    self_censorship: operator_property?.self_censorship,
  });

  const helixImpId = operator_property?.helix_imp_id;
  const operatorTitle = operator_property?.title;
  const eventLog: EventLog = {
    eventMeta: {
      id: String(series_id),
      type: 'seriesId',
      provider: helixImpId ? 'helix' : '서비스',
      name: title,
      series: title,
      series_id: String(series_id),
      category,
      subcategory: sub_category,
    },
    customProps: {
      landing_path: 'contents', // 작품홈 랜딩
      view_type: 'series_poster_view',
      ...(helixImpId
        ? {
            helix_id: helixImpId,
            helix_yn: 'y',
          }
        : {}),
    },
    click: {
      imp_id: helixImpId ? helixImpId : undefined,
      imp_provider: helixImpId ? 'helix' : undefined,
    },
    viewimp_contents: helixImpId
      ? {
          imp_id: helixImpId,
          imp_provider: 'helix',
          imp_type: 'vimp',
          id: String(series_id),
          type: '작품홈',
          name: title,
        }
      : undefined,
  };

  const altText = getItemAltText({
    title,
    badgeList,
    statusBadge,
    ageGradeBadge,
    rank: service_property?.rank ?? undefined,
    rankingVariation: service_property?.rank_variation,
  });

  return {
    id: `RankingPosterView-itemSeries-${series_id}-${service_property?.rank}-${service_property?.rank_variation}-${helixImpId}-${metaInfoType}`,
    type: ItemType.RankingPosterView,
    ageGrade: createAgeGrade(age_grade),
    selfCensorship: operator_property?.self_censorship,
    title: operatorTitle || title, // 동일작의 경우 운영툴에서 설정한 타이틀을 우선으로 보여줍니다,
    altText,
    badgeList,
    ageGradeBadge,
    statusBadge,
    showPlayerIcon: false,
    thumbnail: thumbnail.startsWith('http')
      ? thumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: thumbnail,
          resolution: 'th3',
        }),
    subtitleList: getSubtitleList({
      itemType: ItemType.PosterView,
      metaInfoType,
      servicePropertyDto: service_property,
      onIssueVo: on_issue,
      authors,
      subCategoryName: sub_category,
      categoryName: category,
    }),
    scheme: `kakaopage://open/content?series_id=${series_id}`,
    rank: service_property?.rank ?? undefined,
    rankVariation: service_property?.rank_variation,
    seriesId: series_id,
    eventLog,
    discountRate: operator_property?.cash_info?.discount_rate,
    discountRateText: getDiscountRateText({
      discountRate: operator_property?.cash_info?.discount_rate,
      setDiscountRate: operator_property?.cash_info?.set_discount_rate,
    }),
  };
}

export function withItemSingleDto(
  userInfo: Maybe<User>,
  {
    age_grade,
    badge,
    product_id,
    thumbnail,
    title,
    operator_property,
    service_property,
    series_property,
    slide_type,
  }: ItemSingleDto
): PosterViewItem {
  const ageGradeVo = getAgeGradeVo({
    singleAgeGrade: age_grade,
    seriesAgeGrade: series_property?.age_grade,
  });
  const {
    is_waitfree = false,
    waitfree_period_by_minute = null,
    business_model,
  } = series_property || {};
  const {badgeList, ageGradeBadge, statusBadge} = getCommonBadge({
    itemType: ItemType.PosterView,
    badge,
    age_grade: ageGradeVo,
    is_waitfree,
    waitfree_period_by_minute,
    business_model,
    self_censorship: operator_property?.self_censorship,
  });

  const eventLog: EventLog = {
    eventMeta: {
      id: String(product_id),
      type: 'productId',
      provider: '서비스',
      series: series_property?.title,
      series_id: String(series_property?.series_id),
    },
    customProps: {
      landing_path: 'viewer', // 뷰어 랜딩
      view_type: 'single_poster_view',
    },
  };

  const altText = getItemAltText({
    title,
    badgeList,
    statusBadge,
    ageGradeBadge,
  });

  return {
    id: `PosterView-itemSingle-${product_id}`,
    type: ItemType.PosterView,
    showPlayerIcon:
      slide_type === SlideTypeVo.TalkHumor ||
      slide_type === SlideTypeVo.VideoOnly,
    ageGrade: createAgeGrade(ageGradeVo),
    selfCensorship: operator_property?.self_censorship,
    title,
    altText,
    thumbnail: thumbnail.startsWith('http')
      ? thumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: thumbnail,
          resolution: 'th3',
        }),
    badgeList,
    ageGradeBadge,
    statusBadge,
    subtitleList: service_property?.view_count
      ? [getFormattedNumberWithSuffix(service_property?.view_count)]
      : [],
    scheme: `kakaopage://open/viewer?product_id=${product_id}&series_id=${series_property?.series_id}`,
    seriesId: series_property?.series_id,
    eventLog,
    discountRate: operator_property?.cash_info?.discount_rate,
    discountRateText: getDiscountRateText({
      discountRate: operator_property?.cash_info?.discount_rate,
      setDiscountRate: operator_property?.cash_info?.set_discount_rate,
    }),
  };
}

export function withSetProductDto(
  userInfo: Maybe<User>,
  {product_id, purchase_info, thumbnail, title}: SetProductDto
): PosterViewItem {
  const altText = getItemAltText({
    title,
  });
  const labelBadge =
    purchase_info?.purchase_type === PurchaseTypeVo.Buy ||
    purchase_info?.purchase_type === PurchaseTypeVo.SetBuy
      ? t('sethome_set_badge_possession')
      : (purchase_info?.purchase_type === PurchaseTypeVo.Rent ||
            purchase_info?.purchase_type === PurchaseTypeVo.SetRent) &&
          // 유저가 아직 열람을 하지 않았거나, 대여기간이 만료되었다면 대여 뱃지를 노출하지 않습니다.
          (!purchase_info.rent_expire_dt ||
            compareAsc(new Date(), parseDate(purchase_info.rent_expire_dt)) < 0)
        ? t('sethome_set_badge_rental')
        : undefined;

  return {
    id: `PosterView-itemSingle-${product_id}`,
    type: ItemType.PosterView,
    title,
    altText,
    thumbnail: thumbnail.startsWith('http')
      ? thumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: thumbnail,
          resolution: 'th3',
        }),
    badgeList: [],
    labelBadgeList: labelBadge ? [labelBadge] : [],
    showPlayerIcon: false,
    // 세트상품에 대해서는 연령 정보 노출을 하지 않아도 되므로 임의의 All로 할당.
    // 추후 연령 정보를 처리해야 한다면 실제 연령 정보를 넣어야 합니다.
    ageGrade: AgeGrade.All,
    showDimmedThumbnail: !!labelBadge,
  };
}

export function withItemSetDto({
  userInfo,
  itemSetDto: {
    authors,
    age_grade,
    series_id,
    thumbnail,
    title,
    operator_property,
    service_property,
    sub_category,
    category,
    discount_rate,
    set_id,
  },
  metaInfoType,
}: {
  userInfo: Maybe<User>;
  itemSetDto: ItemSetDto;
  metaInfoType?: Maybe<MetaInfoType>;
}): PosterViewItem {
  const {ageGradeBadge} = getCommonBadge({
    itemType: ItemType.PosterView,
    age_grade,
    is_waitfree: false,
    waitfree_period_by_minute: null,
    self_censorship: operator_property?.self_censorship,
  });

  const operatorTitle = operator_property?.title;

  const eventLog: EventLog = {
    eventMeta: {
      id: String(set_id),
      type: 'setId',
      provider: 'service',
      name: title,
      series_id: String(series_id),
      category,
      subcategory: sub_category,
    },
    customProps: {
      landing_path: 'sets',
    },
  };
  const altText = getItemAltText({
    title,
    ageGradeBadge,
    subtitleList: getSubtitleList({
      itemType: ItemType.PosterView,
      metaInfoType,
      servicePropertyDto: service_property,
      authors,
      subCategoryName: sub_category,
      categoryName: category,
      isAltText: true,
    }),
  });

  return {
    id: `PosterView-itemSet-${series_id}-${metaInfoType}`,
    type: ItemType.PosterView,
    ageGrade: createAgeGrade(age_grade),
    selfCensorship: operator_property?.self_censorship,
    title: operatorTitle || title,
    altText,
    badgeList: [],
    ageGradeBadge,
    showPlayerIcon: false,
    thumbnail: thumbnail.startsWith('http')
      ? thumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: thumbnail,
          resolution: 'th3',
        }),
    subtitleList: getSubtitleList({
      itemType: ItemType.PosterView,
      metaInfoType,
      servicePropertyDto: service_property,
      authors,
      subCategoryName: sub_category,
      categoryName: category,
    }),
    scheme: `kakaopage://open/content/set_overview?set_id=${set_id}`,
    rank: service_property?.rank ?? undefined,
    seriesId: series_id,
    discountRate: discount_rate,
    discountRateText: discount_rate ? `${discount_rate}%` : undefined,
    eventLog,
  };
}
